import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { publicIdSchema } from "./public-id";
import { visitContentQuerySchema } from "./visit-content";

const language = z
  .string()
  .min(1)
  .refine(
    (value) => Array.from(value).length <= 10,
    "Language exceeds 10 characters",
  );
const paging = {
  limit: visitContentQuerySchema.shape.limit,
  offset: z
    .string()
    .regex(/^(0|[1-9][0-9]*)$/)
    .transform(Number)
    .pipe(z.int().nonnegative())
    .default(0),
};
export const templateLibraryCategoriesQuerySchema = z.strictObject({
  ...paging,
  language: language.optional(),
});
export const templateLibraryQuerySchema = templateLibraryCategoriesQuerySchema
  .extend({
    category: z
      .string()
      .refine(
        (value) => Array.from(value).length <= 100,
        "Category exceeds 100 characters",
      )
      .optional(),
    uncategorized: z.literal("true").optional(),
  })
  .refine(
    (value) =>
      value.category === undefined || value.uncategorized === undefined,
    "Category and uncategorized cannot be combined",
  );
export type TemplateLibraryQuery = z.output<typeof templateLibraryQuerySchema>;
export type TemplateLibraryCategoriesQuery = z.output<
  typeof templateLibraryCategoriesQuerySchema
>;
const nextOffset = z.int().nonnegative().nullable();
export const templateLibrarySchema = z.object({
  items: z.array(
    z.object({
      public_id: publicIdSchema,
      name: z.string(),
      headers: z.array(z.string()),
      optionalText: z.string().nullable(),
      category: z.string().nullable(),
      language: z.string().nullable(),
      createdAt: z.iso.datetime(),
    }),
  ),
  nextOffset,
});
export const templateLibraryCategoriesSchema = z.object({
  items: z.array(z.string().nullable()),
  nextOffset,
});
const read = oc.errors({
  BAD_REQUEST: { message: "Invalid request" },
  NOT_FOUND: { message: "Not Found" },
  METHOD_NOT_ALLOWED: { message: "Method Not Allowed" },
  INTERNAL_SERVER_ERROR: { message: "Internal Server Error" },
  UNAUTHORIZED: { message: "Authentication required" },
  FORBIDDEN: { message: "Account required" },
  SERVICE_UNAVAILABLE: { message: "Authentication unavailable" },
});
export const templateLibraryContract = {
  libraryTemplates: read
    .input(
      z.object({
        query: templateLibraryQuerySchema.default({ limit: 50, offset: 0 }),
        body: z.undefined().optional(),
      }),
    )
    .meta(
      openapi({
        method: "GET",
        path: "/api/v2/template-library",
        inputStructure: "detailed",
        spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
      }),
    )
    .output(templateLibrarySchema),
  libraryCategories: read
    .input(
      z.object({
        query: templateLibraryCategoriesQuerySchema.default({
          limit: 50,
          offset: 0,
        }),
        body: z.undefined().optional(),
      }),
    )
    .meta(
      openapi({
        method: "GET",
        path: "/api/v2/template-library/categories",
        inputStructure: "detailed",
        spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
      }),
    )
    .output(templateLibraryCategoriesSchema),
};
